<template>
	<likes-modal v-model="isLikesModalActive"
				 :count="props.participation.likes_count"
				 :url="`/v1/events/${props.event.id}/participations/${props.participation.id}/likes`"/>

	<slide-sheet v-model="isCommentsModalActive" :title="t('label.comments')" :width="480">
		<template #default>
			<comment-feed :url="`/v1/events/${props.event.id}/participations/${props.participation.id}/comments`"
						  v-model:comments-count="commentsCount"
						  v-model:comments="comments"
						  :cache-buster="commentLoaderCacheBuster"
						  @toggle-like="handleLikedComment" class="px-16"/>
		</template>

		<template #footer>
			<div v-if="$auth.loggedIn" class="py-16 pr-16 pl-12 border-t">
				<lazy-comment-form :action="`/v1/events/${props.event.id}/participations/${props.participation.id}/comments`"
								   inline
								   @success="handleCreatedComment"/>
			</div>
			<lazy-login-to-see-more v-else flat no-spacing :text="t('action.login_to_comment')"/>
		</template>
	</slide-sheet>
</template>

<script setup lang="ts">
import type {EventPreview, Participation} from "@spoferan/spoferan-ts-core";
import type {Ref} from "vue";
import {ref, watch} from "vue";
import {useI18n, useCommentable, useNuxtApp} from "#imports";

const props = defineProps<{
	event: EventPreview,
	participation: Participation,

	// Controls the visibility of the likes modal
	likes?: boolean,

	// Controls the visibility of the comments modal
	comments?: boolean,
}>();
const emit = defineEmits(['update:likes', 'update:comments']);

const {t} = useI18n();
const {$date, $auth} = useNuxtApp();

const isLikesModalActive: Ref<boolean> = ref(props.likes);
watch(isLikesModalActive, (newValue: boolean) => {
	emit('update:likes', newValue);
});
watch(() => props.likes, (newValue) => {
	isLikesModalActive.value = newValue;
});

const isCommentsModalActive: Ref<boolean> = ref(props.comments);
watch(isCommentsModalActive, (newValue: boolean) => {
	emit('update:comments', newValue);
});
watch(() => props.comments, (newValue) => {
	isCommentsModalActive.value = newValue;
});

const {comments, commentsCount, handleCreatedComment} = useCommentable(props.participation.comments_count);
watch(commentsCount, (newValue) => {
	if (props.participation) {
		props.participation.comments_count = newValue;
	}
});

// We do not inline the cache buster in the template as it would be reactive and therefore trigger a reload when we call 'updateCacheBuster'
const commentLoaderCacheBuster: Ref<number|undefined> = ref(undefined);

watch(() => props.participation, (newValue) => {
	commentsCount.value = newValue.comments_count;
	commentLoaderCacheBuster.value = newValue.cache_buster;
});

function updateCacheBuster() {
	props.participation.cache_buster = $date().unix();
}

function handleLikedComment() {
	updateCacheBuster();
}

</script>